import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  TablePagination,
  TableSortLabel,
  CircularProgress,
  Tabs,
  Tab,
  useTheme,
  alpha
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import useStaffData from '../../../hooks/useStaffData';
import { styled } from '@mui/material/styles';

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));

const SearchField = styled('input')(({ theme }) => ({
  width: '100%',
  padding: '12px 16px 12px 48px',
  fontSize: '16px',
  borderRadius: '5px',
  border: '1px solid #e0e0e0',
  outline: 'none',
  transition: 'border-color 0.2s, box-shadow 0.2s',
  '&:focus': {
    borderColor: theme.palette.primary.main,
    boxShadow: `0 0 0 2px ${theme.palette.primary.light}40`,
  },
  '&:hover': {
    borderColor: theme.palette.primary.main,
  },
}));

const healthFacilityColumns = [
  { id: 'srNo', label: 'SL NO.', sortable: false },
  { id: 'health_facility', label: 'HEALTH FACILITY NAME', sortable: true },
  { id: 'district', label: 'DISTRICT NAME', sortable: true },
  { id: 'Asha', label: 'ASHA (from Health Facility)', sortable: true },
  { id: 'ANM_CHO', label: 'ANM/CHO (from Health Facility)', sortable: true },
  { id: 'Field_Coordinator_Midori', label: 'FIELD COORDINATOR (from Midori)', sortable: true },
];

const managementColumns = [
  { id: 'srNo', label: 'SL NO.', sortable: false },
  { id: 'staff_name', label: 'STAFF NAME', sortable: true },
  { id: 'designation', label: 'DESIGNATION', sortable: true },
  { id: 'state_district', label: 'STATE/DISTRICT', sortable: true },
];

// ---------- IMPORTANT: TabPanel now uses display: 'none' for inactive panels ----------
function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      // use display instead of hidden attr so inactive panel truly doesn't occupy layout space
      style={{
        display: value === index ? 'flex' : 'none',
        flex: 1,
        flexDirection: 'column',
        minHeight: 0,
        width: '100%',
      }}
      id={`staff-tabpanel-${index}`}
      aria-labelledby={`staff-tab-${index}`}
      {...other}
    >
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, width: '100%' }}>
        {children}
      </Box>
    </div>
  );
}

function a11yProps(index) {
  return {
    id: `staff-tab-${index}`,
    'aria-controls': `staff-tabpanel-${index}`,
  };
}

export default function StaffData() {
  const theme = useTheme();
  const [tabValue, setTabValue] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');

  const {
    data,
    loading,
    error,
    filters,
    handleSort,
    handleChangePage,
    handleChangeRowsPerPage,
    handleSearch,
    handleTabChange: handleApiTabChange,
    totalItems
  } = useStaffData('management');

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    handleApiTabChange(event, newValue === 0 ? 'management' : 'healthFacility');
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    handleSearch(e);
  };

  // client-side slice fallback
  const paginatedData = data.slice(
    filters.page * filters.rowsPerPage,
    (filters.page + 1) * filters.rowsPerPage
  );

  const renderTable = () => {
    const columns = tabValue === 0 ? managementColumns : healthFacilityColumns;

    return (
      <>
        <TableContainer
          sx={{
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            minHeight: 0,
            height: '100%', // ensure it fills the TabPanel vertical space
            overflow: 'auto',
            '& .MuiTable-root': {
              minWidth: 'max-content',
              tableLayout: 'auto',
            },
            '& .MuiTableCell-head': {
              backgroundColor: 'white',
            },
          }}
        >
          {loading ? (
            <Box display="flex" justifyContent="center" p={5}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Box display="flex" justifyContent="center" p={5}>
              <Typography color="error">Error loading data. Please try again later.</Typography>
            </Box>
          ) : (
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  {columns.map((column) => (
                    <TableCell
                      key={column.id}
                      sortDirection={filters.sortField === column.id ? filters.sortOrder : false}
                      sx={{
                        position: 'sticky',
                        top: 0,
                        zIndex: 2,
                        backgroundColor: 'white',
                        color: theme.palette.common.black,
                        fontWeight: 'bold',
                        '& .MuiTableSortLabel-root': {
                          color: 'inherit',
                          '&:hover': {
                            color: alpha(theme.palette.common.black, 0.8),
                          },
                        },
                        '& .MuiTableSortLabel-icon': {
                          color: `${theme.palette.common.black} !important`,
                        },
                      }}
                    >
                      {column.sortable !== false ? (
                        <TableSortLabel
                          active={filters.sortField === column.id}
                          direction={filters.sortField === column.id ? filters.sortOrder : 'asc'}
                          onClick={() => handleSort(column.id)}
                        >
                          {column.label}
                        </TableSortLabel>
                      ) : (
                        column.label
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {data.length > 0 ? (
                  paginatedData.map((row, index) => (
                    <StyledTableRow hover key={index}>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{(filters.page * filters.rowsPerPage) + index + 1}</TableCell>
                      {tabValue === 0 ? (
                        <>
                          <TableCell>{row.staff_name || 'N/A'}</TableCell>
                          <TableCell>{row.designation || 'N/A'}</TableCell>
                          <TableCell>{row.state_district || 'N/A'}</TableCell>
                        </>
                      ) : (
                        <>
                          <TableCell>{row.health_facility || 'N/A'}</TableCell>
                          <TableCell>{row.district || 'N/A'}</TableCell>
                          <TableCell>{row.Asha || 'N/A'}</TableCell>
                          <TableCell>{row.ANM_CHO || 'N/A'}</TableCell>
                          <TableCell>{row.Field_Coordinator_Midori || 'N/A'}</TableCell>
                        </>
                      )}
                    </StyledTableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={columns.length} align="center">
                      No data available
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </TableContainer>

        <Box
          sx={{
            borderTop: '1px solid',
            borderColor: 'divider',
            backgroundColor: 'white',
          }}
        >
          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100]}
            component="div"
            count={totalItems || 0}
            rowsPerPage={filters.rowsPerPage}
            page={filters.page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              '& .MuiTablePagination-toolbar': {
                flexWrap: 'wrap',
                justifyContent: 'center',
                '& > *': {
                  margin: '4px 8px',
                },
              },
            }}
          />
        </Box>
      </>
    );
  };

  return (
    <Box sx={{
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      p: { xs: 0, md: 2 },
      boxSizing: 'border-box',
      minHeight: 0,
      overflow: 'hidden',
      '& .MuiTableContainer-root': {
        '&::-webkit-scrollbar': {
          width: '6px',
        },
        '&::-webkit-scrollbar-track': {
          background: '#f1f1f1',
        },
        '&::-webkit-scrollbar-thumb': {
          background: '#888',
          borderRadius: '3px',
        },
        '&::-webkit-scrollbar-thumb:hover': {
          background: '#555',
        }
      }
    }}>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          aria-label="staff data tabs"
          centered
        >
          <Tab label="Management Staff" {...a11yProps(0)} />
          <Tab label="Health Facility Staff" {...a11yProps(1)} />
        </Tabs>
      </Box>

      <Box sx={{ mb: 3 }}>
        <Box sx={{ position: 'relative' }}>
          <SearchIcon
            sx={{
              position: 'absolute',
              left: 16,
              top: '50%',
              transform: 'translateY(-50%)',
              color: 'text.secondary',
              zIndex: 3,
            }}
          />
          <SearchField
            type="text"
            value={searchTerm}
            onChange={handleSearchChange}
            placeholder={`Search by ${tabValue === 0 ? 'staff name, designation, or district...' : 'health facility, district, or staff type...'}`}
            onFocus={(e) => {
              e.target.style.borderColor = theme.palette.primary.main;
              e.target.style.boxShadow = `0 0 0 2px ${theme.palette.primary.light}40`;
            }}
            onBlur={(e) => {
              e.target.style.borderColor = '#e0e0e0';
              e.target.style.boxShadow = 'none';
            }}
            style={{
              width: '100%',
              padding: '12px 16px 12px 48px',
              fontSize: '16px',
              borderRadius: '5px',
              border: '1px solid #e0e0e0',
              outline: 'none',
              transition: 'border-color 0.2s, box-shadow 0.2s',
            }}
          />
        </Box>
      </Box>

      <Paper sx={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        overflow: 'hidden',
        position: 'relative',
        minHeight: 0,
      }}>
        <TabPanel value={tabValue} index={0}>
          {renderTable()}
        </TabPanel>
        <TabPanel value={tabValue} index={1}>
          {renderTable()}
        </TabPanel>
      </Paper>
    </Box>
  );
}
